<template>
  <v-card elevation="0">
    <v-card-title>
      <span class="text-h4 black--text font-weight-bold">Tags</span>
      <v-spacer></v-spacer>
      <v-text-field
        outlined
        dense
        label="Search Tags..."
        append-icon="mdi-magnify"
        hide-details
        clearable
        v-model="tagSearch"
      ></v-text-field>
      <v-btn icon @click="fetchTags">
        <v-icon> mdi-sync </v-icon>
      </v-btn>
      <v-btn color="primary" @click="editTag()" outlined class="ml-2">
        <v-icon left>mdi-plus</v-icon>Add New Tag
      </v-btn>
    </v-card-title>
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="tagHeaders"
          :items="paginatedTags"
          :loading="tagLoading"
          :server-items-length="totalTags"
          :options.sync="tableOptions"
          :footer-props="{ 'items-per-page-options': [5, 10, 25, 50] }"
        >
          <template v-slot:[`item.name`]="{ item }">
            <span class="font-bigger">
              {{ item.name }}
            </span>
            <v-chip v-if="item.compose_yaml" x-small color="purple" dark class="ml-1">compose</v-chip>
          </template>
          <template v-slot:[`item.label`]="{ item }">
            <span class="font-bigger">
              {{ item.label }}
            </span>
            <v-icon v-if="item.label === null">mdi-minus</v-icon>
          </template>
          <template v-slot:[`item.chipset_ids`]="{ item }">
            <v-chip
              class="ml-1 mb-1 elevation-1"
              :key="index"
              small
              v-for="(chipset, index) in item.chipset_ids"
            >
              {{ shortenChipset(chipset, allDevices) }}
            </v-chip>
            <v-icon v-if="item.chipset_ids.length === 0">mdi-minus</v-icon>
          </template>
          <!-- Where this tag's compatibility data came from. A hand-entered tag and a
               detector-derived one used to look identical here. -->
          <template v-slot:[`item.compat_source`]="{ item }">
            <v-tooltip bottom v-if="item.compat_source === 'override'">
              <template v-slot:activator="{ on, attrs }">
                <v-chip small color="orange" text-color="white" v-bind="attrs" v-on="on">
                  override
                </v-chip>
              </template>
              <span>{{ item.compat_reason || 'no reason recorded' }}</span>
            </v-tooltip>
            <v-tooltip bottom v-else-if="item.compat_source === 'detected'">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  :color="item.compat_confidence >= 0.9 ? 'green' : 'blue-grey'"
                  text-color="white"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ item.compat_status || 'detected' }}
                </v-chip>
              </template>
              <span>
                {{ (item.compat_platforms || []).join(', ') || 'no platform' }}
                &middot; confidence {{ item.compat_confidence }}
              </span>
            </v-tooltip>
            <v-chip small outlined v-else>not detected</v-chip>
          </template>
          <template v-slot:[`item.cuda_major`]="{ item }">
            <span v-if="item.cuda_major">{{ item.cuda_major }}</span>
            <v-icon v-else small>mdi-minus</v-icon>
          </template>
          <template v-slot:[`item.minDriverVersion`]="{ item }">
            {{ item.min_driver_version }}
            <v-icon v-if="!item.min_driver_version">mdi-minus</v-icon>
          </template>
          <template v-slot:[`item.jetpack_tag_assoc`]="{ item }">
            <v-chip
              class="ml-1 mb-1 elevation-1"
              :key="index"
              small
              v-for="(jetpack, index) in getJetpackVersion(item.jetpack)"
            >
              {{ jetpack }}
            </v-chip>
            <v-icon v-if="item.jetpack.length === 0">mdi-minus</v-icon>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon @click="editTag(item)">
              <v-icon>mdi-circle-edit-outline</v-icon>
            </v-btn>
            <v-btn icon @click="removeTag(item)">
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <create-tag-view
      :tag="selectedTag"
      :dockerId="selectedDocker.id"
      :dockerType="selectedDocker.type"
      :showCreateTagView="showEditTag"
      @close="showEditTag = false"
      :allJetpacks="allJetpacks"
      @tag-updated="fetchTags"
    ></create-tag-view>
  </v-card>
</template>

<script>
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'
import CreateTagView from './CreateTagView.vue'
import converter from '../../mixins/converter'
export default {
  components: { CreateTagView },
  props: {
    selectedDocker: {
      type: Object,
      default: () => ({})
    },
    allJetpacks: {
      type: Array,
      default: () => []
    }
  },
  mixins: [converter],
  data () {
    return {
      tagSearch: null,
      tagLoading: false,
      selectedTag: null,
      showEditTag: false,
      paginatedTags: [],
      totalTags: 0,
      // Category list used to turn chipset ids into readable names. `categories_all` rather
      // than `categories` because it includes chipset 0 (Server-Workstation / DGX Spark).
      allDevices: [],
      tableOptions: {
        page: 1,
        itemsPerPage: 10
      },
      searchDebounceTimer: null,
      tagHeaders: [
        {
          text: 'Name',
          value: 'name',
          width: 250
        },
        {
          text: 'Label',
          value: 'label',
          align: 'center'
        },
        {
          text: 'Devices',
          value: 'chipset_ids',
          align: 'center',
          sortable: false
        },
        {
          text: 'Source',
          value: 'compat_source',
          align: 'center',
          sortable: false
        },
        {
          text: 'CUDA',
          value: 'cuda_major',
          align: 'center',
          sortable: false
        },
        {
          text: 'Uncompressed Size (BYTE)',
          value: 'uncompressed_size',
          align: 'center',
          sortable: false
        },
        {
          text: 'Jetpack',
          value: 'jetpack_tag_assoc',
          align: 'center',
          sortable: false
        },
        {
          text: 'Min Driver Version',
          value: 'minDriverVersion',
          align: 'center',
          sortable: false
        },
        {
          text: 'Actions',
          value: 'actions',
          align: 'center',
          sortable: false,
          width: 110
        }
      ]
    }
  },
  methods: {
    removeTag (selectedTag) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'You wont be able to revert this!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!'
      }).then(async (result) => {
        const payload = {
          tagId: selectedTag.id
        }
        if (result.value) {
          await apiService
            .post('admin/delete-tag', payload)
            .then((res) => {
              this.fetchTags()
              Swal.fire('Deleted!', 'Licence has been deleted.', 'success')
            })
            .catch((err) => {
              console.error('Error at delete tag: ', err)
              Swal.showValidationMessage(`Request failed: ${err}`)
            })
        }
      })
    },
    async fetchDevices () {
      try {
        const res = await apiService.get('admin/get-docker-sources')
        this.allDevices = res.data.data.categories_all || res.data.data.categories || []
      } catch (e) {
        // Names are a nicety: without them shortenChipset falls back to the raw id.
        this.allDevices = []
      }
    },

    editTag (tag) {
      this.selectedTag = tag
      this.showEditTag = true
    },
    async fetchTags () {
      if (!this.selectedDocker?.id) return
      this.tagLoading = true
      const { page, itemsPerPage } = this.tableOptions
      try {
        const res = await apiService.get(
          `admin/docker-tags/${this.selectedDocker.id}`,
          {
            params: {
              page,
              itemsPerPage,
              search: this.tagSearch || ''
            }
          }
        )
        this.paginatedTags = res.data.tags
        this.totalTags = res.data.total
      } catch (err) {
        console.error('Error fetching tags:', err)
      } finally {
        this.tagLoading = false
      }
    },
    getJetpackVersion (item) {
      return item.map((val) => {
        return val.version
      })
    }
  },
  watch: {
    'selectedDocker.id': {
      handler (newVal) {
        if (newVal) {
          this.fetchTags()
        }
      },
      immediate: true
    },
    tableOptions: {
      handler () {
        this.fetchTags()
      },
      deep: true
    },
    tagSearch () {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.tableOptions.page = 1
        this.fetchTags()
      }, 400)
    }
  },
  mounted () {
    // fetchTags is triggered by the selectedDocker.id watcher with immediate: true
    this.fetchDevices()
  },
  beforeDestroy () {
    clearTimeout(this.searchDebounceTimer)
  }
}
</script>

<style>
</style>
